import { Container, Divider } from "@material-ui/core";
import { Box, Grid } from "@mui/material";
import { default as axios } from "axios";
import {
  CustomTable,
  FormCard,
  FormLabel,
  FormUploadImage,
  ModalError,
  ModalSuccess,
  NumberInput,
  Page,
  PasswordInput,
  PrimaryButton,
  SectionLabel,
  SkeletonComponent,
  TextInput,
} from "components";
import { HeaderTitle } from "layouts";
import { useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors, validatePassword } from "utils";

function Profile() {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const roleId = localStorage?.getItem("role_id");
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataProfile, setDataProfile] = useState({
    full_name: "",
    email: "",
    phone: "",
  });
  const [profileImage, setProfileImage] = useState({
    image_url: null,
    image: null,
  });
  const [passwordError, setPasswordError] = useState(null);
  const [password, setPassword] = useState({
    new_password: "",
    confirm_new_password: "",
  });
  const [dataTable, setDataTable] = useState([]);

  const handleChangeDataProfile = (value, key) => {
    setDataProfile(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };
  const handleChangePassword = (value, key) => {
    setPassword(currentState => ({
      ...currentState,
      [key]: value,
    }));
    setPasswordError(null);
  };

  const getDetail = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/me/${roleId}`, {
        headers,
      });
      const { data } = res?.data;
      setDataProfile({
        full_name: data?.name,
        email: data?.email,
        phone: data?.phone,
      });
      setProfileImage(currentState => ({
        ...currentState,
        image_url: data?.display_picture_url,
      }));
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getAppVersion = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/version`, {
        headers,
      });
      const { data } = res?.data;
      setDataTable([
        {
          name: "Front End Version",
          version: data?.fe,
        },
        {
          name: "Back End Version",
          version: data?.api,
        },
      ]);
    } catch (error) {
      new Error("Error fetching version");
      // ModalError(getErrors(error?.response));
    }
  };
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getDetail(), getAppVersion()];

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSaveProfileData = async () => {
    try {
      await axios.put(`${hardBaseUrl}/update-profile`, dataProfile, {
        headers,
      });
      ModalSuccess("Successfully updated profile").then(() => fetchData());
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const handleSaveProfilePicture = async () => {
    try {
      const formData = new FormData();
      formData.append("profile_image", profileImage?.image);
      await axios.put(`${hardBaseUrl}/update-image`, formData, {
        headers,
      });
      ModalSuccess("Successfully updated profile picture").then(() =>
        fetchData()
      );
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const handleSavePassword = async () => {
    const { new_password, confirm_new_password } = password;

    const validationError = validatePassword({
      password: new_password,
      confirmPassword: confirm_new_password,
      minLength: 8,
      strongPassword: true,
    });
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    try {
      await axios.put(
        `${hardBaseUrl}/change-password`,
        { new_password },
        { headers }
      );
      ModalSuccess("Successfully update password").then(() => fetchData());
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Page className={classes.root} title="Profile">
      {loadingPage ? (
        <SkeletonComponent variant="rect" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle title="Profile" />
          <Divider className={classes.divider} />
          <SectionLabel
            title="Account Information"
            subTitle="Features to make changes to profile data"
          />
          <FormCard maxWidth="70%">
            <Grid container columnSpacing={2} rowSpacing={2}>
              {MockFieldFormAccountInformation?.map(
                ({ key, title, disabled, type }, index) => (
                  <Grid
                    item
                    xs={12}
                    md={key !== "full_name" ? 6 : 12}
                    key={index}
                  >
                    <FormLabel label={title} required />
                    {type === "number" ? (
                      <NumberInput
                        placeholder={title}
                        autoComplete={`new-${key}`}
                        value={dataProfile?.[key]}
                        onChange={e => handleChangeDataProfile(e, key)}
                      />
                    ) : (
                      <TextInput
                        placeholder={title}
                        value={dataProfile?.[key]}
                        onChange={e =>
                          handleChangeDataProfile(e?.target?.value, key)
                        }
                        disabled={disabled}
                      />
                    )}
                  </Grid>
                )
              )}
            </Grid>
          </FormCard>
          <PrimaryButton label="Save Changes" onClick={handleSaveProfileData} />
          <Divider className={classes?.divider} />
          <SectionLabel
            title="Profile Picture"
            subTitle="Features to make profile photo changes"
          />
          <FormCard maxWidth="70%">
            <FormUploadImage
              label="Profile Picture"
              imageUrl={profileImage.image_url}
              setImageLogo={setProfileImage}
              caption="Click to change profile picture"
              width={200}
              height={200}
              fileSize={5}
            />
          </FormCard>
          <PrimaryButton
            label="Save Changes"
            onClick={handleSaveProfilePicture}
          />
          <Divider className={classes?.divider} />
          <SectionLabel
            title="Change Password"
            subTitle="Features to make a new password change"
          />
          <FormCard maxWidth="70%">
            <Grid container columnSpacing={2}>
              {MockFieldFormPassword?.map(({ key, title }, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <FormLabel label={title} />
                  <PasswordInput
                    autoComplete={key}
                    value={password?.[key]}
                    onChange={e => handleChangePassword(e?.target?.value, key)}
                    placeholder={title}
                    error={!!passwordError}
                    helperText={
                      passwordError ||
                      (key === "new_password" && "Minimum 8 Characters")
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </FormCard>
          <PrimaryButton label="Save Changes" onClick={handleSavePassword} />
          <Divider className={classes?.divider} />
          <SectionLabel title="CARIS Version" />
          <Box maxWidth="70%" mt="16px">
            <CustomTable columnTable={columnTable} data={dataTable} />
          </Box>
        </Container>
      )}
    </Page>
  );
}
const columnTable = [
  {
    name: "name",
    title: "Version",
    width: 150,
    renderText: item => <b>{item}</b>,
  },
  {
    name: "version",
    title: "",
  },
];
const MockFieldFormAccountInformation = [
  {
    title: "Full Name",
    key: "full_name",
  },
  {
    title: "Email",
    key: "email",
    disabled: true,
  },
  {
    title: "Phone Number",
    key: "phone",
    type: "number",
  },
];
const MockFieldFormPassword = [
  {
    title: "New Password",
    key: "new_password",
  },
  {
    title: "Confirm New Password",
    key: "confirm_new_password",
  },
];
export default Profile;
